import { useCallback, useState } from 'react'
import { CharacterLimitCounter, Dropdown, TextEditor } from 'ui_kit_qmd'
import { ConfirmationPopup } from '../confirmation-popup/confirmationpopup'
import Portal from '../portal/Portal'
import cautionIcon from '../../images/common/caution.png'
import { useVisitCancellationReasons } from './useVisitCancellationReasons'
import { text } from './constants'
import './CancelVisitPopup.scss'

const MAX_COMMENT_LENGTH = 100
const OTHER_REASON_PREFIX = 'other'

interface Props {
  closeAction: () => void
  cancelAction: (reasonId: string, comment?: string) => void
  isReviewed: boolean
}

export const CancelVisitPopup = ({ closeAction, cancelAction, isReviewed }: Props) => {
  // Warmed on login (AuthenticatedLayout) and cached for the session, so the list is normally
  // already here on open. `isWithGeneratedOnTheFlyOptions` on the Dropdown below keeps it in
  // sync if the data ever arrives after mount.
  const { data: allReasons = [] } = useVisitCancellationReasons()
  // Regular visits offer every reason; reviewed (async) visits offer only the reasons flagged
  // with AppliesToAsyncVisit.
  const reasons = isReviewed ? allReasons.filter((item) => item.AppliesToAsyncVisit) : allReasons

  const [reason, setReason] = useState('')
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [dropdownError, setDropdownError] = useState('')
  const [textError, setTextError] = useState('')

  const handleReasonChange = useCallback((value: string) => {
    setReason(value)
    setDropdownError('')
  }, [])

  const handleTextChange = useCallback((value: string) => {
    setAdditionalInfo(value)
    setTextError('')
  }, [])

  const handleConfirmCancellation = useCallback(() => {
    const selectedReason = reasons.find((item) => item.Text === reason)
    if (!selectedReason?.Id) {
      setDropdownError(text.REASON_REQUIRED_ERROR)
      return
    }
    // Notes are optional, except when the reason is "Other" (matched by prefix so backend
    // variants like "Other (please specify)" still require notes).
    const isOther = (selectedReason.Text ?? '').trim().toLowerCase().startsWith(OTHER_REASON_PREFIX)
    if (isOther && !additionalInfo.length) {
      setTextError(text.NOTES_REQUIRED_ERROR)
      return
    }
    if (additionalInfo.length > MAX_COMMENT_LENGTH) {
      setTextError(text.CHARACTER_LIMIT_ERROR)
      return
    }
    cancelAction(selectedReason.Id, additionalInfo || undefined)
    closeAction()
  }, [reasons, reason, additionalInfo, cancelAction, closeAction])

  return (
    <Portal>
      <ConfirmationPopup
        title={text.CONFIRM_CANCELLATION}
        titleIcon={cautionIcon}
        className='cancel-visit-pop-up-container'
        id='admin-cancel-visit-pop-up'
        primaryBtnText={text.CONFIRM_CANCELLATION}
        primaryBtnAction={handleConfirmCancellation}
        secondaryBtnText={text.GO_BACK}
        secondaryBtnAction={closeAction}
        closeAction={undefined}
        isCaution
        msg={
          <div className='cancel-visit-pop-up-message-container'>
            <div className='cancel-visit-pop-up-message'>{text.CONFIRM_DELETION_MESSAGE}</div>
            <Dropdown
              options={reasons.map((item) => item.Text ?? '')}
              value={reason}
              setValue={handleReasonChange}
              label={text.SELECT_REASON_LABEL}
              containerStyle={{ width: '100%' }}
              localError={dropdownError}
              // Reasons are warmed on login, but this keeps the options in sync in the rare
              // case the data lands after the popup has mounted.
              isWithGeneratedOnTheFlyOptions
            />
            <div>
              <TextEditor
                placeHolderText={text.ADDITIONAL_NOTES_PLACEHOLDER}
                id='cancelVisitPopUpTextEditor'
                setText={handleTextChange}
                descriptionLocalError={textError}
                isError={textError}
              />
              <CharacterLimitCounter
                currentAmount={additionalInfo.length}
                limit={MAX_COMMENT_LENGTH}
                isHighlightError={additionalInfo.length > MAX_COMMENT_LENGTH}
              />
            </div>
            <div className='cancel-visit-pop-up-question'>{text.CANCELLATION_QUESTION}</div>
          </div>
        }
        popUpBoxStyle={undefined}
        extraView={undefined}
        buttonWidth={undefined}
      />
    </Portal>
  )
}
